import { ClarificationQuestion, ClarificationQuestionItem, IDialogItem, IMessagePart } from '../types';

const isClarificationBlock = (block: { data_type?: string }): block is ClarificationQuestion =>
    block.data_type === 'clarification_question';

export function getClarificationQuestions(block: ClarificationQuestion): ClarificationQuestionItem[] {
    const raw: unknown[] = Array.isArray(block.data) ? block.data : [block.data];

    return (
        raw
            .filter(
                (q): q is ClarificationQuestionItem =>
                    !!q && typeof q === 'object' && typeof (q as ClarificationQuestionItem).question === 'string',
            )
            .map((q) => (Array.isArray(q.options) ? q : { ...q, options: [] }))
            // Row 2022: details are kept only as one string per option; anything else is dropped and the card shows its options alone
            .map((q) => {
                const details = q.option_details;
                const usable =
                    Array.isArray(details) &&
                    details.length === q.options.length &&
                    details.every((d) => typeof d === 'string');
                if (usable || details === undefined) return q;
                const { option_details: _dropped, ...rest } = q;
                return rest;
            })
    );
}

export function findClarificationBlock(item: IDialogItem): ClarificationQuestion | undefined {
    return item.parts
        .filter((p) => p.type === 'json' && p.jsonBlocks)
        .flatMap((p) => p.jsonBlocks!)
        .find(isClarificationBlock);
}

/**
 * The response with its clarification block replaced by the first question's text, so the chat
 * history shows that question; later questions are added to the dialog as they are answered.
 */
export function withClarificationQuestionText(item: IDialogItem): IDialogItem | null {
    const block = findClarificationBlock(item);
    if (!block) return null;

    const parts: IMessagePart[] = item.parts.flatMap((part) => {
        if (part.type !== 'json' || !part.jsonBlocks?.some(isClarificationBlock)) return [part];
        const rest = part.jsonBlocks.filter((b) => !isClarificationBlock(b));
        return rest.length > 0 ? [{ ...part, jsonBlocks: rest }] : [];
    });

    const question = getClarificationQuestions(block)[0]?.question ?? '';
    if (question) {
        parts.push({
            type: 'markdown',
            message: parts.length > 0 ? `\n\n${question}` : question,
            source: 'backend',
        });
    }

    return { ...item, isClarificationQuestion: true, parts };
}
